import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImagePlus, MapPin, Send, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Button from './Button';
import { useAuth } from '../context/AuthContext';
import { useAssistantContext } from '../context/AssistantContext';
import { useFeatureFlag } from '../utils/useFeatureFlag';
import {
  Approval,
  AssistantLimitReached,
  AssistantMessage,
  AssistantUsage,
  PendingAction,
  buildImagePart,
  fetchAssistantUsage,
  parseDataUrl,
  sendAssistantMessage,
} from '../services/assistantService';
import MarkdownMessage from './MarkdownMessage';
import CoCaptainIcon from './CoCaptainIcon';

export const ASSISTANT_FEATURE_FLAG = 'captain-ai-assistant';

interface Bubble {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  // Data URLs of any images the captain attached to this (user) turn, shown as
  // thumbnails. Assistant bubbles never carry images.
  images?: string[];
}

// One pending image attachment, before it's sent. `base64` is the raw payload
// (no data-URL prefix) used to build the Gemini part; `dataUrl` is for preview.
interface Attachment {
  id: number;
  dataUrl: string;
  mimeType: string;
  base64: string;
  name: string;
}

const STARTER_PROMPTS = [
  "Who hasn't set availability for the next match?",
  'Suggest a lineup for the next match',
  'Which matches still need a lineup?',
];

// Image-upload limits, mirrored (more strictly) by the backend. Base64 is ~1.33x
// the file size, and the whole conversation replays each turn, so keep these modest.
const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 4 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif'];

const NUDGE_KEY = 'coCaptainNudgeDismissed';

/** Read a File into a `data:<mime>;base64,...` URL. */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Whether a drag event is carrying files (vs. selected text or other content),
 *  so the panel only reacts to file drags. */
function dragHasFiles(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

/** A stable id for one chat conversation, sent as PostHog's $ai_session_id so
 *  every turn groups into a single AI-observability session. */
function createSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the non-crypto id */
  }
  return `cc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

const ChatPanel: React.FC = () => {
  const { context } = useAssistantContext();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<AssistantUsage | null>(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(NUDGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [showNudge, setShowNudge] = useState(false);
  // Images staged for the next turn, plus a transient validation message.
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  // True while a file is dragged over the panel, to show the drop overlay.
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  // dragenter/dragleave also fire for child elements; count nesting depth so the
  // overlay only clears when the cursor truly leaves the panel.
  const dragDepthRef = useRef(0);

  // Canonical Gemini-format history, replayed to the server each turn.
  const messagesRef = useRef<AssistantMessage[]>([]);
  const bubbleIdRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentIdRef = useRef(0);
  const currentAssistantRef = useRef<number | null>(null);
  // Last approval value sent — preserved so a manual retry replays the same
  // confirm/cancel decision (and not "no approval") after a handle-decision turn fails.
  const lastApprovalRef = useRef<Approval | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  // Auto-scroll target: the message list container. We "stick to bottom" so
  // streaming responses stay in view, but only if the user hasn't deliberately
  // scrolled up — see the effect below for the threshold.
  const scrollRef = useRef<HTMLDivElement>(null);
  // One session id per conversation (this mount), stable across turns.
  const sessionIdRef = useRef<string>('');
  if (!sessionIdRef.current) sessionIdRef.current = createSessionId();

  const addBubble = useCallback(
    (role: 'user' | 'assistant', text: string, images?: string[]): number => {
      const id = (bubbleIdRef.current += 1);
      setBubbles((prev) => [...prev, { id, role, text, images }]);
      return id;
    },
    []
  );

  const appendToAssistant = useCallback((text: string) => {
    const id = currentAssistantRef.current;
    if (id == null) return;
    setBubbles((prev) => prev.map((b) => (b.id === id ? { ...b, text: b.text + text } : b)));
  }, []);

  const runTurn = useCallback(
    async (approval?: Approval) => {
      lastApprovalRef.current = approval;
      setLoading(true);
      setRetrying(false);
      setError(null);
      setPending(null);
      currentAssistantRef.current = addBubble('assistant', '');

      await sendAssistantMessage(
        {
          messages: messagesRef.current,
          clientContext: context,
          approval: approval ?? null,
          sessionId: sessionIdRef.current,
        },
        {
          onText: (text) => {
            // First bytes flowing — auto-retry window is over, drop the indicator.
            setRetrying(false);
            appendToAssistant(text);
          },
          onPendingAction: setPending,
          onMessages: (messages) => {
            messagesRef.current = messages;
          },
          onUsage: setUsage,
          onLimitReached: (info: AssistantLimitReached) => {
            setUsage({
              spent_usd: info.spent_usd,
              limit_usd: info.limit_usd,
              remaining_usd: info.remaining_usd,
              percent_used: info.percent_used,
              is_blocked: true,
              is_warning: true,
              admin_email: info.admin_email,
            });
          },
          onError: setError,
          onRetry: () => setRetrying(true),
        }
      );

      setLoading(false);
      setRetrying(false);
      // Return focus to the input so the captain can keep typing (clicking
      // Confirm/Cancel or the disabled-during-send state can steal focus).
      inputRef.current?.focus();
    },
    [addBubble, appendToAssistant, context]
  );

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if ((!trimmed && attachments.length === 0) || loading) return;
      setInput('');
      setAttachError(null);
      if (attachments.length > 0) {
        // Gemini `parts` form: images first, then the (optional) caption. The
        // backend passes `parts` through and decodes the base64 at call time.
        const parts: unknown[] = attachments.map((a) => buildImagePart(a.mimeType, a.base64));
        if (trimmed) parts.push({ text: trimmed });
        messagesRef.current = [...messagesRef.current, { role: 'user', parts }];
        addBubble(
          'user',
          trimmed,
          attachments.map((a) => a.dataUrl)
        );
        setAttachments([]);
      } else {
        // Text-only turns keep the simple shape the server normalizes.
        messagesRef.current = [...messagesRef.current, { role: 'user', content: trimmed }];
        addBubble('user', trimmed);
      }
      await runTurn();
    },
    [addBubble, attachments, loading, runTurn]
  );

  // Validate, read, and stage image files (from the picker or a paste). Rejects
  // non-images, oversized files, and anything past the per-turn cap with a small
  // inline message; the backend re-checks all of this as the real boundary.
  const addFiles = useCallback(
    async (files: File[]) => {
      setAttachError(null);
      const room = MAX_IMAGES - attachments.length;
      if (room <= 0) {
        setAttachError(`You can attach up to ${MAX_IMAGES} images.`);
        return;
      }
      const accepted: Attachment[] = [];
      for (const file of files) {
        if (accepted.length >= room) break;
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          setAttachError('Only PNG, JPEG, WebP, or HEIC images can be attached.');
          continue;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          setAttachError('Images must be 4 MB or smaller.');
          continue;
        }
        try {
          const dataUrl = await readFileAsDataUrl(file);
          const parsed = parseDataUrl(dataUrl);
          if (!parsed) continue;
          accepted.push({
            id: (attachmentIdRef.current += 1),
            dataUrl,
            mimeType: parsed.mimeType,
            base64: parsed.base64,
            name: file.name || 'image',
          });
        } catch {
          /* unreadable file — skip it */
        }
      }
      if (files.length > room) setAttachError(`You can attach up to ${MAX_IMAGES} images.`);
      if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
    },
    [attachments.length]
  );

  const removeAttachment = useCallback((id: number) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length) void addFiles(files);
      // Clear so picking the same file again still fires onChange.
      e.target.value = '';
    },
    [addFiles]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length) {
        e.preventDefault();
        void addFiles(files);
      }
    },
    [addFiles]
  );

  // Drag-and-drop: accept image files dropped anywhere on the open panel, in
  // addition to the file picker and paste. Reuses the same validation pipeline.
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (usage?.is_blocked || !dragHasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setIsDraggingFile(true);
    },
    [usage?.is_blocked]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (usage?.is_blocked || !dragHasFiles(e)) return;
      e.preventDefault(); // required so the drop event fires
      e.dataTransfer.dropEffect = 'copy';
    },
    [usage?.is_blocked]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!dragHasFiles(e)) return;
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDraggingFile(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      dragDepthRef.current = 0;
      setIsDraggingFile(false);
      if (usage?.is_blocked) return;
      e.preventDefault();
      const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
      if (files.length) void addFiles(files);
    },
    [addFiles, usage?.is_blocked]
  );

  const handleDecision = useCallback(
    async (decision: 'approved' | 'rejected') => {
      if (!pending) return;
      await runTurn({ decision });
    },
    [pending, runTurn]
  );

  const handleRetry = useCallback(async () => {
    if (loading) return;
    // Reuse the partial assistant bubble (if any) by clearing its text — the
    // replayed turn will refill it, avoiding orphan typing dots.
    const id = currentAssistantRef.current;
    if (id != null) {
      setBubbles((prev) => prev.map((b) => (b.id === id ? { ...b, text: '' } : b)));
    }
    await runTurn(lastApprovalRef.current);
  }, [loading, runTurn]);

  const dismissNudge = useCallback(() => {
    setShowNudge(false);
    setNudgeDismissed(true);
    try {
      // Per-session: reappears on a fresh visit, stays hidden for this session.
      sessionStorage.setItem(NUDGE_KEY, '1');
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    dismissNudge();
  }, [dismissNudge]);

  // Gently reveal the attention nudge a moment after the closed launcher appears.
  useEffect(() => {
    if (open || nudgeDismissed) {
      setShowNudge(false);
      return;
    }
    const timer = setTimeout(() => setShowNudge(true), 700);
    return () => clearTimeout(timer);
  }, [open, nudgeDismissed]);

  // Load the user's current monthly spend the first time they open the panel,
  // so the usage indicator (and any pre-existing block state) is accurate even
  // before they send a turn.
  useEffect(() => {
    if (!open || usage !== null) return;
    let cancelled = false;
    fetchAssistantUsage().then((snapshot) => {
      if (!cancelled && snapshot) setUsage(snapshot);
    });
    return () => {
      cancelled = true;
    };
  }, [open, usage]);

  // Keep the message list pinned to the bottom as the assistant streams. If
  // the user has scrolled up by more than ~120px we assume they're reading
  // something earlier and don't yank them back down.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 120) {
      el.scrollTop = el.scrollHeight;
    }
  }, [bubbles, pending, error]);

  if (!open) {
    return (
      <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2">
        {showNudge && (
          <div
            data-testid="assistant-nudge"
            className="cc-bubble-in relative mr-1 max-w-[15rem] rounded-2xl rounded-br-sm border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          >
            <button
              type="button"
              aria-label="Dismiss"
              onClick={dismissNudge}
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow hover:text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              <X className="h-3 w-3" />
            </button>
            <button type="button" onClick={handleOpen} className="block text-left">
              Need a hand with your team?{' '}
              <span className="font-medium text-[#19a75c]">Ask your AI Co-Captain</span> 🎾
            </button>
          </div>
        )}
        <button
          type="button"
          aria-label="Open AI Co-Captain"
          onClick={handleOpen}
          className="cc-float flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-[#19a75c] shadow-lg transition-transform hover:scale-110 hover:bg-[#168a4d]"
        >
          <CoCaptainIcon className="h-9 w-9 text-white" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="assistant-panel"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="cc-pop-in fixed bottom-0 right-0 z-40 flex h-[32rem] w-full max-w-md flex-col rounded-t-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 sm:bottom-5 sm:right-5 sm:rounded-xl"
    >
      {isDraggingFile && (
        <div
          data-testid="assistant-drop-overlay"
          className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#19a75c] bg-white/90 text-[#19a75c] dark:bg-gray-900/90"
        >
          <ImagePlus className="h-8 w-8" />
          <span className="text-sm font-medium">Drop image to attach</span>
        </div>
      )}
      <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <CoCaptainIcon className="h-6 w-6 text-[#19a75c]" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Co-Captain</h2>
        </div>
        <button
          type="button"
          aria-label="Close AI Co-Captain"
          onClick={() => setOpen(false)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div
        data-testid="assistant-context"
        className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50 px-4 py-1.5 text-xs text-gray-500 dark:border-gray-800 dark:bg-gray-800/50 dark:text-gray-400"
        title="What the assistant is currently looking at"
      >
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">
          {context.visible_summary
            ? `Viewing ${context.visible_summary}`
            : 'No team selected yet'}
        </span>
      </div>

      {usage && usage.is_blocked && (
        <div
          data-testid="assistant-blocked-banner"
          role="alert"
          className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
        >
          <p className="font-medium">You've reached your monthly AI limit.</p>
          <p>
            Spent ${usage.spent_usd.toFixed(2)} of ${usage.limit_usd.toFixed(2)}. Contact{' '}
            <a
              className="underline"
              href={`mailto:${usage.admin_email ?? 'greg.henkhaus@gmail.com'}?subject=Raise%20my%20AI%20Co-Captain%20limit`}
            >
              {usage.admin_email ?? 'greg.henkhaus@gmail.com'}
            </a>{' '}
            to raise it.
          </p>
        </div>
      )}

      {usage && !usage.is_blocked && usage.is_warning && (
        <div
          data-testid="assistant-warning-banner"
          className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
        >
          You're close to your monthly AI limit (${usage.spent_usd.toFixed(2)} of $
          {usage.limit_usd.toFixed(2)} used).
        </div>
      )}

      {usage && !usage.is_blocked && !usage.is_warning && (
        <div
          data-testid="assistant-usage-meter"
          className="border-b border-gray-100 bg-gray-50 px-4 py-1 text-[0.6875rem] text-gray-500 dark:border-gray-800 dark:bg-gray-800/30 dark:text-gray-400"
        >
          ${usage.spent_usd.toFixed(2)} of ${usage.limit_usd.toFixed(2)} used this month
        </div>
      )}

      <div
        ref={scrollRef}
        data-testid="assistant-message-list"
        className="flex-1 space-y-3 overflow-y-auto px-4 py-3"
      >
        {bubbles.map((bubble) => {
          if (bubble.role === 'assistant' && bubble.text === '' && !loading) return null;
          const isUser = bubble.role === 'user';
          return (
            <div
              key={bubble.id}
              data-testid="assistant-message"
              data-role={bubble.role}
              className={
                'cc-bubble-in ' +
                (isUser
                  ? 'ml-auto max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-gray-900 px-3 py-2 text-sm text-white dark:bg-gray-700'
                  : 'mr-auto max-w-[85%] rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-900 dark:bg-gray-800 dark:text-gray-100')
              }
            >
              {bubble.role === 'assistant' && bubble.text === '' ? (
                <span
                  data-testid="assistant-typing"
                  className="flex items-center gap-2 py-1 text-xs text-gray-500 dark:text-gray-400"
                >
                  <span className="flex items-center gap-1">
                    <span className="cc-dot h-1.5 w-1.5 rounded-full bg-gray-400" />
                    <span
                      className="cc-dot h-1.5 w-1.5 rounded-full bg-gray-400"
                      style={{ animationDelay: '0.15s' }}
                    />
                    <span
                      className="cc-dot h-1.5 w-1.5 rounded-full bg-gray-400"
                      style={{ animationDelay: '0.3s' }}
                    />
                  </span>
                  {retrying && <span data-testid="assistant-retrying">Retrying…</span>}
                </span>
              ) : (
                <>
                  {bubble.images && bubble.images.length > 0 && (
                    <div
                      data-testid="assistant-message-images"
                      className="mb-1.5 flex flex-wrap gap-1.5"
                    >
                      {bubble.images.map((src, i) => (
                        <img
                          key={i}
                          src={src}
                          alt="attachment"
                          className="max-h-40 max-w-[12rem] rounded-md object-cover"
                        />
                      ))}
                    </div>
                  )}
                  {isUser ? bubble.text : <MarkdownMessage text={bubble.text} />}
                </>
              )}
            </div>
          );
        })}

        {pending && pending.actions.length > 0 && (
          <div
            data-testid="assistant-pending-action"
            className="cc-bubble-in rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-sm dark:border-amber-700 dark:bg-amber-950"
          >
            <p className="mb-2 font-medium text-amber-900 dark:text-amber-200">
              {pending.actions.length === 1
                ? 'Confirm this change?'
                : `Confirm these ${pending.actions.length} changes?`}
            </p>
            <ul className="mb-3 space-y-1 text-amber-800 dark:text-amber-300">
              {pending.actions.map((action, i) => (
                <li key={i} className="flex gap-1.5">
                  <span aria-hidden>•</span>
                  <span>{action.summary}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Button size="sm" variant="primary" onClick={() => handleDecision('approved')}>
                {pending.actions.length === 1 ? 'Confirm' : 'Confirm all'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleDecision('rejected')}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="flex items-center justify-between gap-2 text-sm text-red-600 dark:text-red-400"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={handleRetry}
              disabled={loading}
              className="shrink-0 rounded-md border border-red-300 px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950"
            >
              Retry
            </button>
          </div>
        )}

        {bubbles.length === 0 && !loading && !usage?.is_blocked && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">Try asking:</p>
            {STARTER_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => handleSend(prompt)}
                className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}
      </div>

      <form
        className="flex flex-col gap-2 border-t border-gray-200 px-3 py-3 dark:border-gray-700"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend(input);
        }}
      >
        {(attachments.length > 0 || attachError) && (
          <div className="space-y-1.5">
            {attachments.length > 0 && (
              <div data-testid="assistant-attachments" className="flex flex-wrap gap-2">
                {attachments.map((a) => (
                  <div key={a.id} className="relative h-14 w-14">
                    <img
                      src={a.dataUrl}
                      alt={a.name}
                      className="h-14 w-14 rounded-md border border-gray-200 object-cover dark:border-gray-700"
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${a.name}`}
                      onClick={() => removeAttachment(a.id)}
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow hover:text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {attachError && (
              <p role="alert" className="text-xs text-red-600 dark:text-red-400">
                {attachError}
              </p>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
          <button
            type="button"
            aria-label="Attach image"
            title="Attach an image"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || Boolean(usage?.is_blocked) || attachments.length >= MAX_IMAGES}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-40 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <ImagePlus className="h-5 w-5" />
          </button>
          <input
            ref={inputRef}
            aria-label="Message AI Co-Captain"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPaste={handlePaste}
            placeholder={usage?.is_blocked ? 'Monthly AI limit reached' : 'Ask about your team…'}
            disabled={usage?.is_blocked}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-800/50"
          />
          <Button
            type="submit"
            size="sm"
            aria-label="Send"
            disabled={
              loading || (!input.trim() && attachments.length === 0) || Boolean(usage?.is_blocked)
            }
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

/** Feature-flagged, auth-gated mount point. Only shown on the team management
 *  pages (/teams/*), since the assistant's tools operate on the selected team. */
export const Assistant: React.FC = () => {
  const enabled = useFeatureFlag(ASSISTANT_FEATURE_FLAG, false);
  const { user } = useAuth();
  const { pathname } = useLocation();
  const onTeamPages = pathname === '/teams' || pathname.startsWith('/teams/');
  if (!enabled || !user || !onTeamPages) return null;
  return <ChatPanel />;
};

export default ChatPanel;
